import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useI18nContext } from '@/i18n/i18n-react';
import { analyticsControllerGetTenantUserMetrics } from '@/generated/korza-api-client';
import type { TenantUserMetricsResponseDto } from '@/generated/korza-api-client/types.gen';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { useTenant } from '@/hooks/use-tenant';
import { formatNumber } from '@/components/metrics/formatters';
import { GrainOverlay } from '@/components/brand/grain-overlay';
import { HomeCardLink } from '@/components/home/home-card-link';
import {
  APP_CARD_BACKGROUND,
  APP_CARD_PALETTE,
  appCardWhite,
} from '@/components/home/app-card-palette';

const { text, surfaces } = APP_CARD_PALETTE;
const SECONDARY = { color: appCardWhite(text.secondary) };
const RULE = 'rgba(255,255,255,0.12)';

function AppleIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="size-4"
      fill="currentColor"
    >
      <path d="M16.37 12.6c-.02-2.2 1.8-3.26 1.88-3.31-1.03-1.5-2.62-1.7-3.18-1.73-1.35-.14-2.64.8-3.33.8-.69 0-1.74-.78-2.87-.76-1.47.02-2.83.86-3.59 2.18-1.53 2.66-.39 6.6 1.1 8.76.73 1.06 1.6 2.24 2.73 2.2 1.1-.04 1.51-.71 2.84-.71 1.32 0 1.7.71 2.86.69 1.18-.02 1.93-1.07 2.65-2.13.84-1.22 1.18-2.41 1.2-2.47-.03-.01-2.3-.88-2.32-3.5zM14.2 6.12c.6-.73 1.01-1.75.9-2.76-.87.04-1.92.58-2.54 1.31-.56.65-1.05 1.69-.92 2.69.97.07 1.96-.5 2.56-1.24z" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="size-3.5"
      fill="currentColor"
    >
      <path d="M4.6 3.2c-.2.2-.3.6-.3 1v15.6c0 .4.1.8.3 1l8.7-8.8L4.6 3.2zm9.7 8.8 2.8-2.8-11-6.3c-.3-.2-.6-.2-.9-.2l9.1 9.3zm0 0-9.1 9.3c.3 0 .6 0 .9-.2l11-6.3-2.8-2.8zm3.7-2.3-3.1 2.3 3.1 2.3 3-1.7c.8-.5.8-1.3 0-1.8l-3-1.1z" />
    </svg>
  );
}

function StoreButton({
  href,
  label,
  icon,
}: {
  href: string;
  label: string;
  icon: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex h-10 items-center gap-2 rounded-md border px-3.5 text-[13px] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white max-md:min-h-touch"
      style={{
        borderColor: 'rgba(255,255,255,0.32)',
        backgroundColor: appCardWhite(surfaces.button),
      }}
    >
      {icon}
      {label}
    </a>
  );
}

/**
 * "Seu app" (HOME-09): the brand's dark card with the app icon, name,
 * publication state and version, the all-time totals of /metrics/users
 * (only with analytics on), the store buttons for the URLs that exist (or
 * the "under construction" text) and "Personalizar o app".
 */
export function AppCard({ range }: { range: { from: string; to: string } }) {
  const { LL, locale } = useI18nContext();
  const tenant = useTenant();
  const flags = useFeatureFlags();
  const analyticsEnabled = flags.data?.analyticsEnabled === true;

  // Same key as the Analytics page, so the cache is shared.
  const users = useQuery<TenantUserMetricsResponseDto>({
    queryKey: ['userMetrics', tenant.slug, range.from, range.to],
    queryFn: async () => {
      const res = await analyticsControllerGetTenantUserMetrics({
        path: { tenantSlug: tenant.slug },
        query: { fromDate: range.from, toDate: range.to },
        throwOnError: true,
      });
      return res.data;
    },
    enabled: analyticsEnabled,
  });

  const hasStoreUrl = Boolean(tenant.appStoreAppUrl || tenant.googlePlayAppUrl);
  const stats =
    analyticsEnabled && users.data
      ? [
          {
            value: users.data.totalDownloads,
            label: LL.home.appCard.totalDownloads(),
          },
          {
            value: users.data.usersWithEnabledNotifications.count,
            label: LL.home.appCard.notificationsEnabled(),
          },
        ]
      : [];

  return (
    <section
      aria-label={LL.home.appCard.title()}
      data-home-app-card
      className="relative flex min-w-0 flex-col overflow-hidden rounded-lg text-white shadow-[0_24px_40px_-28px_rgba(11,52,140,0.6)]"
      style={{ background: APP_CARD_BACKGROUND }}
    >
      {/* Texture only; the contrast is measured without it. */}
      <GrainOverlay />
      <div
        className="relative flex h-10 shrink-0 border-b text-[10.5px] font-semibold uppercase tracking-[0.16em]"
        style={{ borderColor: RULE }}
      >
        <span
          className="flex items-center border-r px-[18px]"
          style={{ borderColor: RULE }}
        >
          {LL.home.appCard.title()}
        </span>
        <span className="flex items-center px-[18px]" style={SECONDARY}>
          {LL.home.appCard.platforms()}
        </span>
      </div>
      <div className="relative flex flex-1 flex-col gap-[18px] p-5 max-md:p-4">
        <div className="flex items-center gap-3.5">
          <img
            src={tenant.iconUrl}
            alt=""
            className="size-[52px] shrink-0 rounded-xl shadow-[0_10px_24px_-10px_rgba(0,0,0,0.6)]"
          />
          <span className="flex min-w-0 flex-col gap-1.5">
            <span className="break-words text-lg font-semibold tracking-[-0.01em]">
              {tenant.name}
            </span>
            <span className="flex flex-wrap items-center gap-2">
              <span
                className="inline-flex h-[22px] items-center gap-1.5 rounded-sm px-[7px] text-[11px] font-semibold"
                style={{ backgroundColor: appCardWhite(surfaces.chip) }}
              >
                <span
                  aria-hidden="true"
                  className="size-1.5 rounded-full bg-[#8DB4FF]"
                />
                {tenant.isAppUnderConstruction
                  ? LL.home.appCard.underConstruction()
                  : LL.home.appCard.published()}
              </span>
              {tenant.appVersion ? (
                <span className="text-xs" style={SECONDARY}>
                  {LL.home.appCard.version({ version: tenant.appVersion })}
                </span>
              ) : null}
            </span>
          </span>
        </div>
        {stats.length > 0 ? (
          <div className="grid grid-cols-2 gap-2.5">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="flex min-w-0 flex-col gap-0.5 rounded-md px-3.5 py-3"
                style={{
                  backgroundColor: appCardWhite(surfaces.tile),
                  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.1)',
                }}
              >
                <span className="text-[22px] font-semibold tabular-nums">
                  {formatNumber(stat.value, locale)}
                </span>
                <span className="text-xs" style={SECONDARY}>
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {hasStoreUrl ? (
          <div className="flex flex-wrap gap-2.5">
            {tenant.appStoreAppUrl ? (
              <StoreButton
                href={tenant.appStoreAppUrl}
                label="App Store"
                icon={<AppleIcon />}
              />
            ) : null}
            {tenant.googlePlayAppUrl ? (
              <StoreButton
                href={tenant.googlePlayAppUrl}
                label="Google Play"
                icon={<PlayIcon />}
              />
            ) : null}
          </div>
        ) : tenant.isAppUnderConstruction ? (
          <p className="text-[13px] leading-relaxed" style={SECONDARY}>
            {LL.home.appCard.notInStoresYet()}
          </p>
        ) : null}
        <HomeCardLink
          to={`/${tenant.slug}/customization`}
          className="mt-auto text-white hover:text-white focus-visible:ring-white"
        >
          {LL.home.appCard.customize()}
        </HomeCardLink>
      </div>
    </section>
  );
}
